import { defineFeature, defineModule, definePermission } from '@shared/contract'

// Kasse der Märkte. Jeder Kassenvorgang wird an einer Signatureinheit je Markt abgesichert (Cloud, Anbieter oder
// Simulator; docs/kasse/SPEZIFIKATION.md). Solange ein Betrieb keine Einheit im Echtbetrieb eingerichtet hat, sagen
// jeder Bildschirm und jeder Bon, dass es Schulung bzw. Demo ist. Die Kasse verkauft mit Verbindung zum Server und nur
// in einer offenen Schicht. Ausnahme ist der Störfall: Eine Offline-Kasse (an genau ein Gerät gebunden, je Betrieb
// ausdrücklich eingeschaltet) zeichnet weiter auf dem Gerät auf, wenn es den Server technisch nicht erreicht – ohne
// Absicherung, mit Kennzeichen auf dem Bon, nie nachträglich signiert (docs/kasse-offline/SPEZIFIKATION.md).
//
// Rechte – alle je Standort vergebbar (db/seeds/demo/roles.ts, Abschnitt „Kasse“):
//   * `sale.create` kassiert an den Kassen des Standorts; Rabatte nur bis `maxDiscountBp` aus `discount.grant`.
//     Darüber meldet sich eine Aufsicht an derselben Kasse an und führt den Schritt mit ihrem Recht aus.
//   * `void.create` nimmt Positionen vor der Zahlung wieder vom Bon oder bricht den Bon ab – jedes Mal vermerkt.
//   * Schicht: öffnen, zählen und abschließen sind getrennte Rechte; eine Differenz über der Grenze der Kasse gibt
//     frei, wer `shift.approve` hält und an der Schicht nicht beteiligt war.
const GROUP = 'Kasse'

export default defineModule({
  id: 'pos',
  title: 'Kasse',
  description: 'Kasse der Märkte: Schichten, Verkauf, Zahlarten, Gutscheine, Rücknahme, Kassenbuch, Kassenabschluss, Signatureinheit je Markt und Export für die Betriebsprüfung.',
  dependsOn: ['platform', 'masterdata'],
  // Pfand & Leergut ist ein Branchenmodul: Die Kasse läuft auch ohne (dann ohne Pfandzeilen und Leergutrücknahme)
  optionalDependsOn: ['deposits'],
  requiresFeature: 'pos',
  features: [
    defineFeature({ key: 'pos', label: 'Kasse', description: 'Kasse je Filiale mit Schichten und Kassenabschluss.', defaultEnabled: false }),
  ],
  permissions: [
    definePermission({
      key: 'pos.sale.create',
      label: 'An der Kasse verkaufen',
      group: GROUP,
      description: 'Bons erfassen, parken und kassieren – nur in einer offenen Schicht.',
    }),
    definePermission({
      key: 'pos.return.create',
      label: 'Ware gegen Bon zurücknehmen',
      group: GROUP,
      description: 'Rücknahme einzelner Positionen eines Bons; erstattet wird in der ursprünglichen Zahlart.',
    }),
    definePermission({
      key: 'pos.discount.grant',
      label: 'Rabatte an der Kasse gewähren',
      group: GROUP,
      description: 'Rabatt je Position oder auf den Bon bis zur Grenze; darüber meldet sich eine Aufsicht an der Kasse an.',
      limits: [{ key: 'maxDiscountBp', label: 'Höchster Rabatt an der Kasse', unit: 'bp' }],
    }),
    definePermission({
      key: 'pos.void.create',
      label: 'Positionen und Bons vor der Zahlung stornieren',
      group: GROUP,
      description: 'Jede Stornierung steht im Abschlussbericht der Schicht.',
    }),
    definePermission({
      key: 'pos.shift.read',
      label: 'Schichten, Bons und Kassenbuch lesen',
      group: GROUP,
    }),
    definePermission({ key: 'pos.shift.open', label: 'Kassenschicht öffnen', group: GROUP }),
    definePermission({
      key: 'pos.shift.count',
      label: 'Kasse zählen und Schicht abschließen',
      group: GROUP,
    }),
    definePermission({
      key: 'pos.shift.approve',
      label: 'Kassendifferenzen freigeben',
      group: GROUP,
      description: 'Gibt den Abschluss einer Schicht frei, deren Differenz über der Grenze der Kasse liegt – nie die eigene Schicht.',
    }),
    definePermission({
      key: 'pos.cash.move',
      label: 'Bargeld einlegen und entnehmen',
      group: GROUP,
      description: 'Einlage, Entnahme, Geldtransit zur Bank und Auszahlungen aus der Kasse – jeweils mit Grund.',
    }),
    definePermission({
      key: 'pos.fiscal.manage',
      label: 'Signatureinheiten der Kassen einrichten',
      group: GROUP,
      description: 'Signatureinheit je Markt einrichten (Simulator oder Anbieter), Kassen anmelden, Selbsttest, Ausfall üben. Zugangsdaten werden nur versiegelt gespeichert.',
      sensitive: true,
    }),
    definePermission({
      key: 'pos.export.create',
      label: 'Kassendaten für die Betriebsprüfung exportieren',
      group: GROUP,
      description: 'Export der Kassenabschlüsse mit Einzelaufzeichnungen und Export der Signatureinheit erstellen und herunterladen.',
    }),
    definePermission({
      key: 'pos.register.manage',
      label: 'Kassen einrichten',
      group: GROUP,
      description: 'Kassen eines Standorts anlegen, Schnelltasten belegen, Wechselgeld und Differenzgrenze festlegen.',
    }),
    // Kasse ohne Verbindung (docs/kasse-offline/SPEZIFIKATION.md §14.3) – ein Störfallprozess, je Betrieb ausdrücklich
    definePermission({
      key: 'pos.offline.settings',
      label: 'Betrieb ohne Verbindung einschalten und Grenzen festlegen',
      group: GROUP,
      description: 'Schaltet für den Betrieb ein, dass Offline-Kassen bei Verbindungsverlust weiterverkaufen – nur mit Steuerkanzlei und Datum der Freigabe – und legt die Grenzen fest.',
      locationScoped: false,
      sensitive: true,
    }),
    definePermission({
      key: 'pos.offline.manage',
      label: 'Offline-Kassen einrichten, abmelden und als verloren melden',
      group: GROUP,
      description: 'Ein Gerät an eine Kasse binden, es abmelden, als defekt oder verloren melden und eine Sperre für den Betrieb ohne Verbindung aufheben.',
      sensitive: true,
    }),
    definePermission({
      key: 'pos.offline.resolve',
      label: 'Klärfälle der Offline-Kassen bearbeiten, Ausfälle dokumentieren und Offline-Daten einlesen',
      group: GROUP,
      description: 'Abweichungen beim Übertragen der Bons ohne Verbindung erledigen, Ursache und Maßnahmen eines Ausfalls festhalten und eine Datei-Sicherung eines Geräts übertragen.',
    }),
  ],
})
